// Менюто в хедъра на колоната: сортиране, филтър, скриване.
//
// Стои в antd `Popover` нарочно — `SharedAccountGate` разпознава отворен antd
// слой по DOM и не пуска Esc към глобалната навигация „назад". Собствен попъп
// би счупил точно тази стълбица. По същата причина подсказката е antd
// `Tooltip`, а не собствен балон.

import { useEffect, useRef, useState } from "react";
import { Popover, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CaretDownOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
} from "@ant-design/icons";

import { ColumnFilterControl } from "./ColumnFilterControl";
import { isEmptyFilterValue } from "./filterFns";
import type { ColumnFilterSpec } from "./types";

type Props = {
  label: string;
  filterSpec?: ColumnFilterSpec;
  filterValue: unknown;
  onFilterChange: (value: unknown) => void;
  facets?: Map<string, number> | null;
  bounds?: [number, number] | null;
  sortDirection: "asc" | "desc" | false;
  canSort: boolean;
  onSort: (direction: "asc" | "desc" | null) => void;
  canHide: boolean;
  onHide: () => void;
};

const menuItemClass =
  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12.5px] font-semibold text-ink transition-colors hover:bg-shade-1 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent";

export const ColumnMenu = ({
  label,
  filterSpec,
  filterValue,
  onFilterChange,
  facets,
  bounds,
  sortDirection,
  canSort,
  onSort,
  canHide,
  onHide,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isFiltered = !isEmptyFilterValue(filterValue);

  // Esc затваря МЕНЮТО и спира събитието — иначе същото натискане минава и през
  // глобалната навигация „назад" (правилото важи за всеки custom Esc handler).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const content = (
    <div className="flex min-w-[196px] flex-col gap-1 py-0.5">
      <p className="px-2.5 pb-1 font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
        {label.toUpperCase()}
      </p>

      {canSort ? (
        <>
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              onSort(sortDirection === "asc" ? null : "asc");
              setOpen(false);
            }}
          >
            <ArrowUpOutlined className="text-[12px] text-muted" />
            {t("dataTable.sortAsc")}
            {sortDirection === "asc" ? (
              <span className="ml-auto text-brand-ink">•</span>
            ) : null}
          </button>
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              onSort(sortDirection === "desc" ? null : "desc");
              setOpen(false);
            }}
          >
            <ArrowDownOutlined className="text-[12px] text-muted" />
            {t("dataTable.sortDesc")}
            {sortDirection === "desc" ? (
              <span className="ml-auto text-brand-ink">•</span>
            ) : null}
          </button>
        </>
      ) : null}

      {filterSpec ? (
        <>
          <div className="my-1 h-px bg-shade-2" />
          <div className="px-2.5 pb-1">
            <ColumnFilterControl
              spec={filterSpec}
              value={filterValue}
              onChange={onFilterChange}
              facets={facets}
              bounds={bounds}
            />
          </div>
          {isFiltered ? (
            <button
              type="button"
              className={menuItemClass}
              onClick={() => onFilterChange(undefined)}
            >
              <FilterOutlined className="text-[12px] text-muted" />
              {t("dataTable.clearColumnFilter")}
            </button>
          ) : null}
        </>
      ) : null}

      {canHide ? (
        <>
          <div className="my-1 h-px bg-shade-2" />
          <button
            type="button"
            className={menuItemClass}
            onClick={() => {
              onHide();
              setOpen(false);
            }}
          >
            <EyeInvisibleOutlined className="text-[12px] text-muted" />
            {t("dataTable.hideColumn")}
          </button>
        </>
      ) : null}
    </div>
  );

  return (
    <Tooltip
      title={t("dataTable.columnMenuHint")}
      placement="top"
      mouseEnterDelay={0.4}
      // Отвори ли се менюто, подсказката няма какво да казва и би легнала
      // върху самото меню. `undefined` я връща на неконтролирано поведение.
      open={open ? false : undefined}
    >
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomLeft"
        content={content}
        arrow={false}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-label={t("dataTable.columnMenuFor", { column: label })}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(event) => event.stopPropagation()}
          // ВИДИМ в покой на всички устройства — иначе човек не подозира, че
          // колоната изобщо се подрежда, филтрира и скрива. Тих е с цвят
          // (text-faint), не със скриване: по-плътен на hover и при фокус от
          // клавиатурата. Фунийка + каре е един и същ знак навсякъде:
          // фунийката казва „филтър", карето — „оттук се отваря меню".
          // Активният филтър го боядисва в брандовия цвят.
          className={`inline-flex h-[22px] shrink-0 items-center gap-px rounded-md px-[2px] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-ink ${
            isFiltered
              ? "bg-brand-tint text-brand-ink"
              : open
                ? "bg-shade-2 text-ink"
                : "text-faint hover:bg-shade-2 hover:text-soft focus-visible:text-soft"
          }`}
        >
          <FilterOutlined className="text-[11px]" />
          <CaretDownOutlined className="text-[7px]" />
        </button>
      </Popover>
    </Tooltip>
  );
};
